import type { JSX } from 'react';
import type { LayoutItem } from 'react-grid-layout';
import {
  type DashboardComponentType,
  type IDashboardComponentWithSource,
} from '../../../services/dashboardService';
import { getEntityIconForCode } from '../../settings/EntityCell';
import CountPanel from './panels/CountPanel';
import BarChartPanel from './panels/BarChartPanel';

export interface PanelDefinition {
  key: string;
  title: string;
  type: DashboardComponentType;
  defaultLayout: Omit<LayoutItem, 'i'>;
  icon: JSX.Element;
  // `refreshKey` is forwarded to the panel so the global Refresh can re-run its
  // fetches in place (rather than remounting and aborting in-flight requests).
  render: (refreshKey?: number) => JSX.Element;
}

// A group of panels sharing the same chart type, for the customize drawer.
export interface PanelGroup {
  type: DashboardComponentType;
  label: string;
  panels: PanelDefinition[];
}

// Human label for a chart type section header.
const TYPE_LABELS: Record<DashboardComponentType, string> = {
  COUNT: 'Counts',
  BARCHART: 'Charts',
};

// Drop the trailing type token so the title shows only the entity, e.g.
// "WORK_COUNT" -> "Work", "ASSETS_BARCHART" -> "Assets".
const COMPONENT_TYPE_SUFFIXES = ['COUNT', 'BARCHART'];
export const getComponentTitle = (code: string): string => {
  const parts = (code ?? '').split('_').filter(Boolean);
  if (
    parts.length > 1 &&
    COMPONENT_TYPE_SUFFIXES.includes(parts[parts.length - 1])
  ) {
    parts.pop();
  }
  return parts
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
};

// Pure: group panels by chart type into stable sections; items inside every
// section are sorted by title using the same comparator so ordering is
// consistent across sections.
export const groupPanelsByType = (panels: PanelDefinition[]): PanelGroup[] => {
  const order: DashboardComponentType[] = ['COUNT', 'BARCHART'];
  const byTitle = (a: PanelDefinition, b: PanelDefinition) =>
    a.title.localeCompare(b.title);
  return order
    .map((type) => ({
      type,
      label: TYPE_LABELS[type],
      panels: panels.filter((p) => p.type === type).sort(byTitle),
    }))
    .filter((group) => group.panels.length > 0);
};

const COUNT_LAYOUT: Omit<LayoutItem, 'i'> = {
  x: 0,
  y: 0,
  w: 3,
  h: 4,
  minW: 2,
  minH: 3,
};
const CHART_LAYOUT: Omit<LayoutItem, 'i'> = {
  x: 0,
  y: 0,
  w: 5,
  h: 6,
  minW: 3,
  minH: 4,
};

// Turn the merged backend catalog into renderable panel definitions. The panel
// `key` is the component code (stable id used for layout + persistence). The
// UI is chosen by `type` (COUNT vs BARCHART) and the icon is resolved from the
// shared entity-icon mapping via the component code's entity prefix.
export const buildPanelCatalog = (
  components: IDashboardComponentWithSource[]
): PanelDefinition[] =>
  components.map((component) => {
    const { name, type, source } = component;
    const title = getComponentTitle(name);
    const icon = getEntityIconForCode(name) as JSX.Element;

    if (type === 'BARCHART') {
      return {
        key: name,
        title,
        type,
        defaultLayout: CHART_LAYOUT,
        icon,
        render: (refreshKey?: number) => (
          <BarChartPanel
            title={title}
            code={name}
            source={source}
            icon={icon}
            refreshKey={refreshKey}
          />
        ),
      };
    }

    return {
      key: name,
      title,
      type,
      defaultLayout: COUNT_LAYOUT,
      icon,
      render: (refreshKey?: number) => (
        <CountPanel
          label={title}
          code={name}
          source={source}
          icon={icon}
          refreshKey={refreshKey}
        />
      ),
    };
  });
